import React from 'react';
import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import { PipelineAPI } from '../../../edge/pipelines/api/PipelineAPI';
import { PIPELINE_STATUS_DATA_MAP } from '../../../edge/pipelines/constants/pipelineStatus';
import { RawIntegration } from '../../../edge/pipelines/interfaces/integration';
import { RawSourceType as EnterpriseRawSourceType } from '../../../edge/sources/interfaces/source';
import { GlobalSearchItem, GlobalSearchRegistryItem } from '../../interfaces/global-search';
import PipelinesAPI from '../../../containers/pipeline/api/PipelinesAPI';
import { RawPipeline } from '../../../containers/pipeline/interfaces/model';
import { RawSourceType } from '../../../containers/pipeline/select-source-type/interfaces/sourceType';
import { SOURCE_TYPES } from '../../../containers/pipeline/interfaces/source-type';
import { GenericListItemContent, GenericListItemContentProps } from './GenericListItemContent/GenericListItemContent';
import { GlobalSearchEntityCommonData } from './interfaces/global-search-entity';
import { _teamSettingsService, TeamSettingsService } from '../../../core/services/team-settings.service';

export interface GlobalSearchPipelineData {
  status: string;
  destination: GlobalSearchEntityCommonData;
  source: GlobalSearchEntityCommonData;
}

export const GLOBAL_SEARCH_PIPELINE_KEY = 'pipelines';

export const GlobalSearchPipelineRegistry: GlobalSearchRegistryItem<GlobalSearchPipelineData> = {
  key: GLOBAL_SEARCH_PIPELINE_KEY,
  title: 'Pipelines',
  maxItemsWhenNoFiltersApplied: 2,
  maxItemsWhenFiltersApplied: 20,
  sortOrder: 1,
  filters: [
    {
      value: GLOBAL_SEARCH_PIPELINE_KEY,
      label: 'Pipelines'
    }
  ],
  viewAllRoute: '/pipeline',
  inMemoryFiltering: true,
  dataResolver: () => {
    const isH20Enabled = (_teamSettingsService as TeamSettingsService)?.isH2OEnabled();

    const promises = [
      PipelinesAPI.getIntegrations(undefined, false)
    ];

    if (isH20Enabled) {
      promises.push(PipelineAPI.getIntegrations());
    }

    return Promise.all(promises)
      .then(([{ data: standardPipelines }, edgePipelineRes]) => {
        const _edgePipelines = isH20Enabled
          ? edgePipelineRes.data
              .filter((p) => !!p.source && !!p.destination)
              .map(GlobalSearchEnterprisePipelineFactory)
          : [];

        const _standardPipelines = standardPipelines.map(GlobalSearchPipelineFactory);

        return [ ..._edgePipelines, ..._standardPipelines ];
      });
  },
  renderItem: (item) => {
    const props: GenericListItemContentProps = {
      suffixIcon: item.icon,
      suffixIconBadge: item.data.source.isEdge ? 'Edge' : undefined,
      primaryText: item.name,
      primaryLogoTooltip: item.data.source.displayType,
      primaryLogoProps: {
        logoURL: item.data.source.logoUrl,
        darkModeLogoURL: item.data.source.darkModeLogoUrl,
        primaryColor: SOURCE_TYPES[item.data.source.type]?.primaryColor,
        darkModePrimaryColor: SOURCE_TYPES[item.data.source.type]?.darkModePrimaryColor
      },
      logoConnector: true,
      logoConnectorColor: item.data.status,
      secondaryText: item.data.destination.name,
      secondaryLogoTooltip: item.data.destination.displayType,
      secondaryLogoProps: {
        logoURL: item.data.destination.logoUrl,
        darkModeLogoURL: item.data.destination.darkModeLogoUrl,
        primaryColor: DESTINATION_TYPES[item.data.destination.type]?.primaryColor,
        darkModePrimaryColor: DESTINATION_TYPES[item.data.destination.type]?.darkModePrimaryColor
      }
    };

    return (
      <GenericListItemContent {...props} />
    );
  }
};

export function GlobalSearchPipelineFactory(
  rawPipeline: RawPipeline
): GlobalSearchItem<GlobalSearchPipelineData> {
  return {
    data: {
      status: rawPipeline.pipeline_status.color_type,
      source: GlobalSearchSourceFactory(rawPipeline as unknown as RawSourceType),
      destination: {
        name: rawPipeline.dest_name,
        type: rawPipeline.dest_type,
        displayType: rawPipeline.dest_type_display,
        logoUrl: rawPipeline.dest_type_logo_url,
        darkModeLogoUrl: rawPipeline.dest_type_dark_mode_logo_url ?? rawPipeline.dest_type_logo_url
      }
    },
    key: `${rawPipeline.source_type}-standard-pipeline-${rawPipeline.seq_id}`,
    name: rawPipeline.source_name,
    exitURL: `/pipeline/${rawPipeline.seq_id}/overview`,
    icon: 'pipeline',
    searchKeywords: [
      rawPipeline.source_name,
      rawPipeline.dest_name,
      rawPipeline.source_type,
      rawPipeline.dest_type
    ]
  };
}

export function GlobalSearchEnterprisePipelineFactory(
  rawPipeline: RawIntegration
): GlobalSearchItem {
  return {
    data: {
      id: rawPipeline.id,
      status: PIPELINE_STATUS_DATA_MAP[rawPipeline.status.type].colorType,
      source: GlobalSearchEnterpriseSourceFactory(rawPipeline.source.source_type),
      destination: {
        name: rawPipeline.destination.name,
        type: rawPipeline.destination.destination_type.type,
        displayType: rawPipeline.destination.destination_type.display_name,
        logoUrl: rawPipeline.destination.destination_type.logo_url,
        darkModeLogoUrl: rawPipeline.destination.destination_type.dark_mode_logo_url
      }
    },
    key: `${rawPipeline.source.source_type.type}-edge-pipeline-${rawPipeline.id}`,
    name: rawPipeline.name,
    exitURL: `/pipeline/edge/${rawPipeline.id}/job-history`,
    icon: 'pipeline',
    searchKeywords: [
      rawPipeline.name,
      rawPipeline.source.name,
      rawPipeline.source.source_type.type,
      rawPipeline.source.source_type.display_name,
      rawPipeline.destination.name,
      rawPipeline.destination.destination_type.type,
      rawPipeline.destination.destination_type.display_name
    ]
  };
}

export function GlobalSearchEnterpriseSourceFactory(
  rawSource: EnterpriseRawSourceType
) {
  return {
    name: rawSource.display_name,
    displayType: rawSource.display_name,
    type: rawSource.type,
    logoUrl: rawSource.logo_url,
    darkModeLogoUrl: rawSource.dark_mode_logo_url,
    isEdge: true
  };
}

export function GlobalSearchSourceFactory(
  rawSource: RawSourceType
) {
  return {
    name: rawSource.source_name,
    displayType: rawSource.source_type_display,
    type: rawSource.source_type,
    logoUrl: rawSource.source_type_logo_url,
    darkModeLogoUrl: rawSource.source_type_dark_mode_logo_url ?? rawSource.source_type_logo_url,
    isEdge: false,
  };
}
